import { useMemo, useState } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { useStockAnalytics } from '../hooks/useStockAnalytics';
import { useStorerooms } from '../hooks/useStockLevels';

// Stock moving in, out, and onto jobs.
//
// Charts are plain divs rather than a charting library: the dashboard has none,
// and pulling one in for a handful of bars would add a dependency to every
// build for something CSS does perfectly well. Same approach as the comms
// analytics screen.
//
// Everything here is DAILY. The ledger was seeded on 2 September, so monthly
// buckets would be a single bar dressed up as a trend — the page says where the
// history starts rather than implying the company did.

const num = (v) => Number(v || 0).toLocaleString('en-GB', { maximumFractionDigits: 0 });
const shortDate = (d) => new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });

function Card({ label, value, tone = 'gray', hint }) {
  const tones = {
    gray: 'border-gray-200 bg-white text-gray-900',
    green: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    rose: 'border-rose-200 bg-rose-50 text-rose-800',
    brand: 'border-brand/30 bg-brand-light/30 text-gray-900',
  };
  return (
    <div className={`rounded-lg border px-4 py-3 ${tones[tone]}`}>
      <div className="text-2xl font-bold tabular-nums">{value}</div>
      <div className="text-xs text-gray-500">{label}</div>
      {hint ? <div className="mt-0.5 text-[11px] text-gray-400">{hint}</div> : null}
    </div>
  );
}

// Two series per day, drawn as opposing bars from a shared baseline so in and
// out can be compared at a glance without a legend to decode.
function InOutChart({ rows }) {
  const max = Math.max(1, ...rows.map((r) => Math.max(Number(r.received) + Number(r.transferred_in),
    Number(r.issued) + Number(r.transferred_out))));
  if (!rows.length) return <p className="p-6 text-center text-sm text-gray-400">No movements in this window.</p>;
  return (
    <div className="flex items-end gap-2 overflow-x-auto p-4" style={{ minHeight: 220 }}>
      {rows.map((r) => {
        const inQty = Number(r.received) + Number(r.transferred_in);
        const outQty = Number(r.issued) + Number(r.transferred_out);
        return (
          <div key={r.day} className="flex min-w-[52px] flex-1 flex-col items-center gap-1">
            <div className="flex h-40 w-full items-end justify-center gap-1">
              <div
                className="w-1/2 rounded-t bg-emerald-500"
                style={{ height: `${(inQty / max) * 100}%` }}
                title={`In ${num(inQty)}`}
              />
              <div
                className="w-1/2 rounded-t bg-brand"
                style={{ height: `${(outQty / max) * 100}%` }}
                title={`Out ${num(outQty)}`}
              />
            </div>
            <div className="text-[10px] tabular-nums text-gray-400">{shortDate(r.day)}</div>
          </div>
        );
      })}
    </div>
  );
}

function RankedBars({ rows, labelKey, subKey, valueKey, tone = 'bg-brand' }) {
  const max = Math.max(1, ...rows.map((r) => Number(r[valueKey])));
  if (!rows.length) return <p className="p-6 text-center text-sm text-gray-400">Nothing to show.</p>;
  return (
    <ul className="divide-y divide-gray-100">
      {rows.map((r, i) => (
        <li key={r[labelKey] || i} className="px-4 py-2">
          <div className="flex items-baseline justify-between gap-3">
            <span className="truncate text-sm text-gray-800">{r[labelKey]}</span>
            <span className="shrink-0 text-sm font-semibold tabular-nums text-gray-900">{num(r[valueKey])}</span>
          </div>
          <div className="mt-1 flex items-center gap-2">
            <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-100">
              <div className={`h-full rounded-full ${tone}`} style={{ width: `${(Number(r[valueKey]) / max) * 100}%` }} />
            </div>
            {subKey ? <span className="w-20 shrink-0 text-right text-[11px] text-gray-400">{r[subKey]}</span> : null}
          </div>
        </li>
      ))}
    </ul>
  );
}

function Panel({ title, subtitle, children }) {
  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
      <div className="border-b border-gray-200 px-4 py-2.5">
        <h2 className="text-sm font-bold text-gray-900">{title}</h2>
        {subtitle ? <p className="text-xs text-gray-500">{subtitle}</p> : null}
      </div>
      {children}
    </div>
  );
}

function StockAnalytics() {
  const [days, setDays] = useState(30);
  const [storeroomId, setStoreroomId] = useState('');
  const { data, isLoading, isFetching, refetch } = useStockAnalytics({ days, storeroomId });
  const { data: storerooms } = useStorerooms();

  const totals = useMemo(() => {
    const d = data?.daily || [];
    const sum = (k) => d.reduce((t, r) => t + Number(r[k] || 0), 0);
    const usage = (data?.usage_daily || []).reduce((t, r) => t + Number(r.units || 0), 0);
    const jobs = (data?.usage_daily || []).reduce((t, r) => t + Number(r.jobs || 0), 0);
    return {
      received: sum('received'), issued: sum('issued'),
      tin: sum('transferred_in'), tout: sum('transferred_out'),
      adjusted: sum('adjusted'), usage, jobs,
    };
  }, [data]);

  const sel = 'rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand';

  return (
    <div className="p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Stock Analytics</h1>
          <p className="mt-1 max-w-3xl text-sm text-gray-500">
            What came in, what went out, and what engineers actually fitted.
            {data?.history_starts ? (
              <> The ledger begins on {new Date(data.history_starts).toLocaleDateString('en-GB')}, when
                ISP stock was first loaded, so there is no history before that.
              </>
            ) : null}
          </p>
        </div>
        <button type="button" onClick={() => refetch()} disabled={isFetching}
          className="inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-2 text-sm font-semibold text-white hover:bg-brand-dark disabled:opacity-50">
          <ArrowPathIcon className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} className={sel}>
          <option value={7}>Last 7 days</option>
          <option value={14}>Last 14 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
        {/* Storeroom narrows the ledger charts only. Job-card usage is recorded
            against a van, not a storeroom, so it would be wrong to filter it
            here and imply the two are the same cut of the data. */}
        <select value={storeroomId} onChange={(e) => setStoreroomId(e.target.value)} className={sel}>
          <option value="">All storerooms</option>
          {(storerooms || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        {storeroomId ? (
          <span className="text-xs text-gray-400">movements filtered; job-card usage stays company-wide</span>
        ) : null}
      </div>

      {isLoading ? (
        <p className="mt-6 text-sm text-gray-400">Loading…</p>
      ) : (
        <>
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <Card label="received" value={num(totals.received)} tone="green" />
            <Card label="issued out" value={num(totals.issued)} tone="brand" />
            <Card label="transferred in" value={num(totals.tin)} />
            <Card label="transferred out" value={num(totals.tout)} />
            <Card label="fitted on jobs" value={num(totals.usage)} hint={`${num(totals.jobs)} job cards`} />
            {/* Kept apart from receipts on purpose: this is how opening balances
                were loaded, not stock arriving at a door. */}
            <Card label="count adjustments" value={num(totals.adjusted)} hint="opening balances, not deliveries" />
          </div>

          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            <Panel title="In and out, by day" subtitle="green = received and transferred in · purple = issued and transferred out">
              <InOutChart rows={data?.daily || []} />
            </Panel>

            <Panel title="Fitted on job cards, by day" subtitle="what engineers recorded using">
              <RankedBars
                rows={(data?.usage_daily || []).map((r) => ({
                  label: shortDate(r.day), units: Number(r.units), jobs: `${num(r.jobs)} jobs`,
                }))}
                labelKey="label" subKey="jobs" valueKey="units" tone="bg-emerald-500"
              />
            </Panel>

            <Panel title="Most used items" subtitle="by units fitted on job cards">
              <RankedBars
                rows={(data?.top_used || []).map((r) => ({
                  label: r.name || r.sku, units: Number(r.units), jobs: `${num(r.jobs)} jobs`,
                }))}
                labelKey="label" subKey="jobs" valueKey="units"
              />
            </Panel>

            <Panel title="Busiest storerooms" subtitle="units issued out, all storerooms regardless of the filter">
              <RankedBars
                rows={(data?.by_storeroom || []).map((r) => ({
                  label: r.name, units: Number(r.issued), received: `${num(r.received)} in`,
                }))}
                labelKey="label" subKey="received" valueKey="units"
              />
            </Panel>
          </div>
        </>
      )}
    </div>
  );
}

export default StockAnalytics;
